import { Line } from 'react-chartjs-2'

import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

import {
  ServiceEffectAiPurchaseAssist,
  ServiceEffectArticleUpload,
  ServiceEffectDataIntegration,
  ServiceEffectGeoLlmo,
  ServiceEffectPersona,
  ServiceEffectRecoMedia,
  ServiceEffectReview,
  ServiceSummaryItem,
  ServiceSummaryKey
} from '@/common/api/params/analytics'

import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'

const fmtJpy = (v: number) =>
  v >= 1_000_000
    ? `¥${(v / 1_000_000).toFixed(1)}M`
    : v >= 1_000
      ? `¥${(v / 1_000).toFixed(1)}K`
      : `¥${Math.round(v).toLocaleString()}`

const EffectPanel = ({ service }: { service: ServiceSummaryItem }) => {
  const e = service.effect
  if (!e) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        事業効果指標は今後対応予定
      </Text>
    )
  }
  switch (service.key) {
    case 'data_integration': {
      const v = e as ServiceEffectDataIntegration
      return (
        <div className='flex gap-4'>
          <Effect label='エラー率' value={`${v.error_rate.toFixed(2)}%`} />
          <Effect label='失敗' value={`${v.failed_count.toLocaleString()}件`} />
        </div>
      )
    }
    case 'reco_media': {
      const v = e as ServiceEffectRecoMedia
      return (
        <div className='flex gap-4'>
          <Effect label='CTR' value={`${v.ctr.toFixed(2)}%`} />
          <Effect label='CVR' value={`${v.cvr.toFixed(2)}%`} />
          <Effect label='貢献売上' value={fmtJpy(v.revenue)} />
        </div>
      )
    }
    case 'article_upload': {
      const v = e as ServiceEffectArticleUpload
      return (
        <div className='flex gap-4'>
          <Effect label='公開率' value={`${v.publish_rate.toFixed(1)}%`} />
          <Effect
            label='公開数'
            value={`${v.published_count.toLocaleString()} / ${v.request_count.toLocaleString()}`}
          />
        </div>
      )
    }
    case 'geo_llmo': {
      const v = e as ServiceEffectGeoLlmo
      return (
        <div className='flex gap-4'>
          <Effect
            label='平均SEOスコア'
            value={v.avg_seo_score != null ? `${v.avg_seo_score.toFixed(1)}` : '-'}
          />
        </div>
      )
    }
    case 'review': {
      const v = e as ServiceEffectReview
      return (
        <div className='flex gap-4'>
          <Effect label='投稿成立' value={`${v.submitted_count.toLocaleString()}件`} />
          <Effect
            label='平均評価'
            value={v.avg_rating != null ? `★${v.avg_rating.toFixed(2)}` : '-'}
          />
          <Effect label='UGC由来CV' value={`${v.ugc_origin_purchase.toLocaleString()}件`} />
        </div>
      )
    }
    case 'ai_purchase_assist': {
      const v = e as ServiceEffectAiPurchaseAssist
      return (
        <div className='flex gap-4'>
          <Effect label='購入CVR' value={`${v.cvr.toFixed(2)}%`} />
          <Effect label='クリック経由CV' value={`${v.via_click_count.toLocaleString()}件`} />
          <Effect label='貢献売上' value={fmtJpy(v.revenue)} />
        </div>
      )
    }
    case 'persona': {
      const v = e as ServiceEffectPersona
      return (
        <div className='flex gap-4'>
          <Effect label='採用ペルソナ' value={`${v.adopted_personas.toLocaleString()}体`} />
          <Effect label='判定対象顧客' value={`${v.judged_customers.toLocaleString()}人`} />
          {/* 名寄せ対象が0のときは率が出せない。0% と誤読させないため「-」で出す */}
          <Effect
            label='名寄せ率'
            value={v.identity_rate != null ? `${v.identity_rate.toFixed(1)}%` : '-'}
          />
        </div>
      )
    }
    default:
      return null
  }
}

const Effect = ({ label, value }: { label: string; value: string }) => (
  <div className='flex flex-col'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    <Text weight='bold' size={14}>
      {value}
    </Text>
  </div>
)

// サービス毎のテーマ色（カード左端のアクセント + スパークライン）
const SERVICE_COLOR: Record<ServiceSummaryKey, string> = {
  data_integration: '#0EA5E9', // sky-500
  reco_media: '#4F46E5', // indigo-600
  article_upload: '#10B981', // emerald-500
  geo_llmo: '#F59E0B', // amber-500
  review: '#EF4444', // red-500
  ai_purchase_assist: '#8B5CF6', // violet-500
  carte: '#EC4899', // pink-500
  persona: '#14B8A6', // teal-500
  mail_reco: '#6366F1' // indigo-500（Reco-Media のオプションのため近い色）
}

// サービス毎の業務量単位
const COUNT_UNIT: Record<ServiceSummaryKey, string> = {
  data_integration: '件',
  reco_media: '回',
  article_upload: '本',
  geo_llmo: '件',
  review: '件',
  ai_purchase_assist: '回',
  carte: '人',
  persona: '人',
  mail_reco: '件'
}

const COUNT_LABEL: Record<ServiceSummaryKey, string> = {
  data_integration: '直近30日 連携',
  reco_media: '直近30日 配信',
  article_upload: '直近30日 記事生成',
  geo_llmo: '直近30日 診断',
  review: '直近30日 レビュー',
  ai_purchase_assist: '直近30日 会話',
  carte: '直近30日 更新ユーザー',
  persona: '直近30日 ペルソナ判定',
  mail_reco: '直近30日 メール開封（受信者ごとの商品決定）'
}

const formatLastUpdate = (iso: string | null): string => {
  if (!iso) return 'データなし'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'データなし'
  const now = new Date()
  const diffH = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60))
  if (diffH < 1) return '1時間以内'
  if (diffH < 24) return `${diffH}時間前`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 30) return `${diffD}日前`
  return d.toLocaleDateString('ja-JP')
}

const sparklineOpts: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    y: { display: false, beginAtZero: true }
  },
  elements: {
    line: { borderWidth: 2, tension: 0.35 },
    point: { radius: 0 }
  }
}

interface Props {
  service: ServiceSummaryItem
}

const ServiceSummaryCard = ({ service }: Props) => {
  const color = SERVICE_COLOR[service.key] || '#64748B'
  const unit = COUNT_UNIT[service.key] || '件'
  const countLabel = COUNT_LABEL[service.key] || '直近30日'
  const trendStr =
    service.trend_pct == null
      ? 'NEW'
      : service.trend_pct > 0
        ? `+${service.trend_pct.toFixed(1)}%`
        : `${service.trend_pct.toFixed(1)}%`
  const trendColor: TextColor =
    service.trend_pct == null
      ? TextColor.SECONDARY
      : service.trend_pct >= 0
        ? TextColor.GREEN
        : TextColor.CRITICAL

  return (
    <Area>
      <div
        className='flex flex-col gap-3 rounded-lg p-2'
        style={{ borderLeft: `4px solid ${color}` }}
      >
        <div className='flex items-center justify-between'>
          <Text weight='bold' size={16}>
            {service.name}
          </Text>
          <Text size={12} color={TextColor.SECONDARY}>
            最終更新: {formatLastUpdate(service.last_update_at)}
          </Text>
        </div>

        <div className='grid grid-cols-3 gap-2'>
          <div className='flex flex-col gap-0.5'>
            <Text size={12} color={TextColor.SECONDARY}>
              採用サイト
            </Text>
            <div className='flex items-baseline gap-1'>
              <Text weight='bold' size={20}>
                {service.enabled_sites}
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                / {service.total_sites}
              </Text>
            </div>
            <Text size={12} color={TextColor.SECONDARY}>
              {service.adoption_rate.toFixed(1)}%
            </Text>
          </div>

          <div className='flex flex-col gap-0.5'>
            <Text size={12} color={TextColor.SECONDARY}>
              30日アクティブ
            </Text>
            <div className='flex items-baseline gap-1'>
              <Text weight='bold' size={20}>
                {service.active_sites_30d}
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                サイト
              </Text>
            </div>
            <Text size={12} color={TextColor.SECONDARY}>
              データ更新あり
            </Text>
          </div>

          <div className='flex flex-col gap-0.5'>
            <Text size={12} color={TextColor.SECONDARY}>
              {countLabel}
            </Text>
            <div className='flex items-baseline gap-1'>
              <Text weight='bold' size={20}>
                {service.count_30d.toLocaleString()}
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                {unit}
              </Text>
            </div>
            <Text size={12} color={trendColor}>
              前30日比 {trendStr}
            </Text>
          </div>
        </div>

        <div
          className='rounded-md p-2'
          style={{ background: `${color}0F`, borderTop: `1px dashed ${color}55` }}
        >
          <Text size={12} color={TextColor.SECONDARY}>
            事業効果（直近30日）
          </Text>
          <div className='mt-1'>
            <EffectPanel service={service} />
          </div>
        </div>

        <div className='h-12 w-full'>
          <Line
            data={{
              labels: service.daily_counts.map((d) => d.date),
              datasets: [
                {
                  data: service.daily_counts.map((d) => d.count),
                  borderColor: color,
                  backgroundColor: `${color}22`,
                  fill: true
                }
              ]
            }}
            options={sparklineOpts}
          />
        </div>
      </div>
    </Area>
  )
}

export default ServiceSummaryCard
